import React from 'react';
import { Draggable } from '@hello-pangea/dnd';

// Design System Components
import { Delete } from 'Components/DesignSystem/Iconography/Icons';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';

// Module Components
import FieldBlockContent from './FieldBlockContent';

// Constants
import {
  STRINGS,
  blockDraggableId,
  getFieldClassName,
  getFieldDefinition,
  isStretchBlock,
} from '../../../constants';

// Types
import { BlockPlacement, FieldConfiguration } from '../../../types';

interface PreviewBlockProps {
  placement: BlockPlacement;
  configuration: FieldConfiguration;
  /** Card-wide: price rows carry the unit of measure (spec R69, R72). */
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const alignmentClass: Record<FieldConfiguration['alignment'], string> = {
  left: 'tw-text-left',
  center: 'tw-text-center',
  right: 'tw-text-right',
};

/**
 * One field's block on the preview card: draggable between regions (R50–R53),
 * clickable to select (R55), and — while selected — carrying its own delete
 * control (R56, R57).
 *
 * The whole block is the drag handle, which is what makes placement
 * keyboard-operable: `react-beautiful-dnd` puts the block in the tab order and
 * owns space-to-lift / arrows-to-move / space-to-drop, so there is no
 * hand-rolled key handling here (ADR-003, R10). Enter selects, because space
 * belongs to the drag.
 *
 * The delete control sits beside the content rather than floating over it — an
 * overlay would need negative offsets, and this repo's Tailwind prefix makes
 * those an easy silent no-op.
 *
 * The selected block carries the accent **ring** and offset this product's editors
 * draw a selected element with (spec R79, R80) — a ring rather than an outline, so
 * the indicator follows the block's rounded corners and reads as the same selection
 * the field row's highlight shows in the pane.
 */
const PreviewBlock = ({
  placement,
  configuration,
  index,
  isSelected,
  onSelect,
  onDelete,
}: PreviewBlockProps) => {
  const definition = getFieldDefinition(placement.fieldId);
  // The element's own selector class (spec R67): the block is what a user's Custom
  // styling targets, so the class the stylesheet names has to be here.
  const elementClass = getFieldClassName(placement.fieldId);
  // A block sized as a fraction of the card's width has to fill its row for the
  // fraction to mean anything — the CTA's width options (spec R70).
  const stretches = isStretchBlock(placement.fieldId);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Draggable draggableId={blockDraggableId(placement.fieldId)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          onKeyDown={handleKeyDown}
          aria-label={definition.name}
          aria-pressed={isSelected}
          className={`${
            stretches ? 'tw-flex tw-w-full' : 'tw-inline-flex'
          } tw-items-start tw-gap-1 tw-max-w-full tw-rounded-sm tw-cursor-grab focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accent ${
            isSelected
              ? 'tw-ring-2 tw-ring-accent tw-ring-offset-1'
              : 'hover:tw-ring-1 hover:tw-ring-edge-strong hover:tw-ring-offset-1'
          } ${snapshot.isDragging ? 'tw-opacity-70' : ''}`}
        >
          <span
            className={`${elementClass} tw-min-w-0 tw-max-w-full ${
              stretches ? 'tw-flex-1' : ''
            } ${alignmentClass[configuration.alignment]}`}
          >
            <FieldBlockContent
              fieldId={placement.fieldId}
              configuration={configuration}
            />
          </span>

          {isSelected && !snapshot.isDragging && (
            <IconButton
              variant="danger-subtle"
              size="sm"
              title={STRINGS.preview.deleteBlock}
              aria-label={`${STRINGS.preview.deleteBlock}: ${definition.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
            >
              <Delete size={16} aria-hidden="true" />
            </IconButton>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default PreviewBlock;
